import type { ReactElement } from "react"
import { useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import {
	IconCalendarXmarkOutline18 as CalendarXmarkIcon,
	IconFlag3Outline18 as MilestoneIcon,
	IconUsersOutline18 as MembersIcon,
	IconPlusOutline18 as PlusIcon,
	IconXmarkOutline18 as RemoveIcon,
	IconCalendarCheckOutline18 as StartIcon,
	IconCalendarOutline18 as TargetIcon,
	IconUserGroupOutline18 as TeamIcon,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import type { Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	Button,
	IconButton,
	Input,
	LinearIcon,
	Popover,
} from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"

import { LabelSelect } from "@/components/label-select"
import { PopoverButton } from "@/components/popover-button"
import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { toast } from "@/components/toast"
import { toggleLabelId } from "@/components/toggle-label-id"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"

type EditableProject = Pick<
	Project,
	| "id"
	| "linearProjectId"
	| "linearTeamIds"
	| "linearTeamNames"
	| "startDate"
	| "targetDate"
>

const PILL_CLASS =
	"flex h-7 items-center gap-1.5 rounded-full border border-gray-200 px-2.5 text-sm text-primary dark:border-gray-700/80"

/** Members, labels and milestones Linear keeps for a project, read live and shared across the page. */
export function useProjectExtras(linearProjectId: string | null | undefined) {
	return api.linear.projectExtras.useQuery(
		{ linearProjectId: linearProjectId ?? "" },
		{ staleTime: 60_000, enabled: !!linearProjectId },
	)
}

/** Whether the project has Linear labels, so a viewer's empty row can hide. */
export function useHasProjectLabels(
	linearProjectId: string | null | undefined,
) {
	const { data } = useProjectExtras(linearProjectId)
	return (data?.labels.length ?? 0) > 0
}

/** Runs a Linear write, shows Linear's refusal as a toast, then refreshes the project's Linear data. */
function useLinearProjectWrite(linearProjectId: string | null | undefined) {
	const utils = api.useUtils()
	return async (write: () => Promise<unknown>) => {
		try {
			await write()
		} catch (error) {
			toast.error({
				title: "Linear did not save this change",
				description: error instanceof Error ? error.message : undefined,
				icon: LinearIcon,
			})
		}
		await utils.linear.projectExtras.invalidate({
			linearProjectId: linearProjectId ?? "",
		})
	}
}

const toDay = (date: Date) => format(date, "yyyy-MM-dd")

/** Reads a Linear calendar day stored at UTC midnight back as that same local day. */
const dayOf = (value: number | string | null | undefined) =>
	value ? parseISO(new Date(value).toISOString().slice(0, 10)) : undefined

/** A calendar popover for one Linear calendar day, with a clear action when set. */
const DayPicker = ({
	value,
	clearLabel,
	disabled,
	onChange,
	children,
}: {
	value: Date | undefined
	clearLabel: string
	disabled?: boolean
	onChange: (day: string | null) => void
	children: ReactElement
}) => {
	const [open, setOpen] = useState(false)
	if (disabled) return children
	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			className="w-auto p-0"
			content={
				<div className="flex flex-col">
					<Calendar
						mode="single"
						selected={value}
						defaultMonth={value}
						onSelect={(next) => {
							if (next) onChange(toDay(next))
							setOpen(false)
						}}
						autoFocus
					/>
					{value && (
						<div className="border-t border-gray-200 p-2 dark:border-gray-700/80">
							<Button
								variant="ghost"
								className="w-full justify-start"
								Icon={CalendarXmarkIcon}
								onClick={() => {
									onChange(null)
									setOpen(false)
								}}
							>
								{clearLabel}
							</Button>
						</div>
					)}
				</div>
			}
		>
			{children}
		</Popover>
	)
}

/** Editable project members as an avatar stack, picked from the people of the project's first team. */
export const ProjectMembersProperty = ({
	project,
}: {
	project: EditableProject
}) => {
	const { role } = useRole()
	const [open, setOpen] = useState(false)
	const { data: extras } = useProjectExtras(project.linearProjectId)
	const write = useLinearProjectWrite(project.linearProjectId)
	const update = api.linear.updateProjectDetails.useMutation()
	const teamId = project.linearTeamIds?.[0]
	const { data: people } = api.linear.members.useQuery(
		{ teamId: teamId ?? "" },
		{ cacheTime: Infinity, staleTime: Infinity, enabled: open && !!teamId },
	)
	const current = extras?.members ?? []
	const memberIds = current.map(({ id }) => id)

	const options = useMemo(
		() =>
			(people ?? [])
				.filter(({ app }) => !app)
				.map(({ id, name, avatarUrl }) => ({
					value: id,
					label: (
						<div className="flex items-center gap-2">
							<Avatar name={name} image={avatarUrl} size="xs" />
							<span className="truncate">{name}</span>
						</div>
					),
				})),
		[people],
	)

	const trigger = (
		<PropertyTrigger
			tooltip={
				current.length > 0
					? `Members: ${current.map(({ name }) => name).join(", ")}`
					: "Members"
			}
			icon={
				current.length > 0 ? (
					<div className="flex items-center">
						{current.slice(0, 3).map((member, index) => (
							<Avatar
								key={member.id}
								name={member.name}
								image={member.avatarUrl}
								size="xs"
								className={cn(
									index > 0 && "-ml-1.5 ring-2 ring-white dark:ring-gray-900",
								)}
							/>
						))}
					</div>
				) : (
					<MembersIcon className={propertyIconClassName} />
				)
			}
		>
			<span className={current.length === 0 ? "text-tertiary" : undefined}>
				{current.length === 0
					? "Members"
					: current.length === 1
						? "1 member"
						: `${current.length} members`}
			</span>
		</PropertyTrigger>
	)

	if (role === "VIEWER") return trigger
	return (
		<PopoverButton
			data={options}
			selectedItems={memberIds}
			isLoading={open && !people}
			onOpenChange={setOpen}
			placeholder="Add members..."
			resetOption="Remove all members"
			resetIcon={MembersIcon}
			onChange={(value) => {
				const next =
					value === null
						? []
						: memberIds.includes(value)
							? memberIds.filter((id) => id !== value)
							: [...memberIds, value]
				void write(() =>
					update.mutateAsync({ projectId: project.id, memberIds: next }),
				)
			}}
		>
			{trigger}
		</PopoverButton>
	)
}

/** Linear's start and target dates, each chip opening its own calendar. */
export const ProjectDatesProperty = ({
	project,
}: {
	project: EditableProject
}) => {
	const { role } = useRole()
	const write = useLinearProjectWrite(project.linearProjectId)
	const update = api.linear.updateProjectDetails.useMutation()
	const start = dayOf(project.startDate)
	const target = dayOf(project.targetDate)
	const isViewer = role === "VIEWER"

	return (
		<div className="flex items-center gap-1">
			<DayPicker
				value={start}
				clearLabel="Remove start date"
				disabled={isViewer}
				onChange={(startDate) =>
					void write(() =>
						update.mutateAsync({ projectId: project.id, startDate }),
					)
				}
			>
				<PropertyTrigger
					tooltip="Start date"
					icon={<StartIcon className={propertyIconClassName} />}
				>
					<span className={start ? undefined : "text-tertiary"}>
						{start ? format(start, "MMM do") : "Start"}
					</span>
				</PropertyTrigger>
			</DayPicker>
			<span className="text-sm text-tertiary">→</span>
			<DayPicker
				value={target}
				clearLabel="Remove target date"
				disabled={isViewer}
				onChange={(targetDate) =>
					void write(() =>
						update.mutateAsync({ projectId: project.id, targetDate }),
					)
				}
			>
				<PropertyTrigger
					tooltip="Target date"
					icon={<TargetIcon className={propertyIconClassName} />}
				>
					<span className={target ? undefined : "text-tertiary"}>
						{target ? format(target, "MMM do") : "Target"}
					</span>
				</PropertyTrigger>
			</DayPicker>
		</div>
	)
}

/** The Linear teams a project belongs to. */
export const ProjectTeamsProperty = ({
	project,
}: {
	project: EditableProject
}) => {
	const teams = project.linearTeamNames ?? []
	if (teams.length === 0) return null
	return (
		<PropertyTrigger
			tooltip={teams.join(", ")}
			icon={<TeamIcon className={propertyIconClassName} />}
		>
			{teams.length === 1 ? teams[0] : `${teams.length} teams`}
		</PropertyTrigger>
	)
}

/** The project's Linear labels as pills: remove one with its cross, add more with the plus. */
export const ProjectLabels = ({ project }: { project: EditableProject }) => {
	const { role } = useRole()
	const { data: extras } = useProjectExtras(project.linearProjectId)
	const { data: allLabels } = api.linear.projectLabels.useQuery(undefined, {
		staleTime: 60_000,
	})
	const write = useLinearProjectWrite(project.linearProjectId)
	const update = api.linear.updateProjectDetails.useMutation()
	const [pendingIds, setPendingIds] = useState<string[] | null>(null)
	const saved = extras?.labels ?? []
	const labelIds = pendingIds ?? saved.map(({ id }) => id)
	const current = pendingIds
		? pendingIds.flatMap(
				(id) =>
					saved.find((label) => label.id === id) ??
					allLabels?.find((label) => label.id === id) ??
					[],
			)
		: saved
	const isViewer = role === "VIEWER"

	const saveLabels = async (next: string[]) => {
		setPendingIds(next)
		await write(() =>
			update.mutateAsync({ projectId: project.id, labelIds: next }),
		)
		setPendingIds(null)
	}

	return (
		<div className="flex min-h-7 flex-wrap items-center gap-1.5">
			{current.map((label) => (
				<div key={label.id} className={cn(PILL_CLASS, "group/label")}>
					<span className="relative flex h-3 w-3 flex-none items-center justify-center">
						<span
							className={cn(
								"h-2 w-2 rounded-full",
								!isViewer && "group-hover/label:opacity-0",
							)}
							style={{ backgroundColor: label.color }}
						/>
						{!isViewer && (
							<button
								type="button"
								aria-label={`Remove ${label.name}`}
								className="absolute inset-0 flex items-center justify-center text-tertiary opacity-0 hover:text-primary focus-visible:opacity-100 group-hover/label:opacity-100"
								onClick={() =>
									void saveLabels(labelIds.filter((id) => id !== label.id))
								}
							>
								<RemoveIcon className="h-3 w-3 flex-none" />
							</button>
						)}
					</span>
					<span className="max-w-48 truncate">{label.name}</span>
				</div>
			))}
			{!isViewer && (
				<LabelSelect
					labels={allLabels ?? []}
					selectedLabelIds={labelIds}
					teamId={project.linearTeamIds?.[0] ?? ""}
					onChange={(labelId) =>
						void saveLabels(toggleLabelId(allLabels ?? [], labelIds, labelId))
					}
					trigger={
						current.length === 0 ? (
							<button
								type="button"
								className={cn(
									PILL_CLASS,
									"text-tertiary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800",
								)}
							>
								<PlusIcon className="h-3.5 w-3.5 flex-none" />
								Add label
							</button>
						) : (
							<button
								type="button"
								aria-label="Add label"
								className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-tertiary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800"
							>
								<PlusIcon className="h-3.5 w-3.5 flex-none" />
							</button>
						)
					}
				/>
			)}
		</div>
	)
}

/** A milestone's target date chip with a fixed width, so rows line up whether a date is set or not. */
const MilestoneDate = ({
	value,
	disabled,
	onChange,
}: {
	value: Date | undefined
	disabled?: boolean
	onChange: (day: string | null) => void
}) => (
	<DayPicker
		value={value}
		clearLabel="Remove target date"
		disabled={disabled}
		onChange={onChange}
	>
		<button
			type="button"
			className={cn(
				"flex h-7 w-28 flex-none items-center gap-1.5 rounded-md px-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800",
				value ? "tabular-nums text-secondary" : "text-tertiary",
			)}
		>
			<TargetIcon className="h-3.5 w-3.5 flex-none" />
			{value ? format(value, "MMM d") : "Target date"}
		</button>
	</DayPicker>
)

/** The project's Linear milestones: add, rename, re-date and remove them in place. */
export const ProjectMilestones = ({
	project,
}: {
	project: EditableProject
}) => {
	const { role } = useRole()
	const { data: extras } = useProjectExtras(project.linearProjectId)
	const write = useLinearProjectWrite(project.linearProjectId)
	const create = api.linear.createProjectMilestone.useMutation()
	const update = api.linear.updateProjectMilestone.useMutation()
	const remove = api.linear.deleteProjectMilestone.useMutation()
	const [draft, setDraft] = useState<{
		name: string
		targetDate?: string
	} | null>(null)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [editName, setEditName] = useState("")
	const [pending, setPending] = useState<{
		name: string
		targetDate?: string
	} | null>(null)
	const milestones = extras?.milestones ?? []
	const isViewer = role === "VIEWER"

	if (!extras || (milestones.length === 0 && isViewer)) return null

	const submitDraft = () => {
		const name = draft?.name.trim()
		if (!name) return
		const targetDate = draft?.targetDate
		setDraft(null)
		setPending({ name, targetDate })
		void write(() =>
			create.mutateAsync({ projectId: project.id, name, targetDate }),
		).then(() => setPending(null))
	}

	const submitRename = (milestoneId: string, previous: string) => {
		const name = editName.trim()
		setEditingId(null)
		if (!name || name === previous) return
		void write(() =>
			update.mutateAsync({ projectId: project.id, milestoneId, name }),
		)
	}

	return (
		<div className="mt-6">
			<div className="mb-1 flex h-7 items-center justify-between">
				<span className="text-sm text-secondary">Milestones</span>
				{!isViewer && (
					<IconButton
						variant="ghost"
						size="sm"
						Icon={PlusIcon}
						aria-label="Add milestone"
						onClick={() => setDraft((current) => current ?? { name: "" })}
					/>
				)}
			</div>
			<div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700/80">
				{milestones.map((milestone) => (
					<div
						key={milestone.id}
						className="group/milestone flex h-10 items-center gap-2.5"
					>
						<MilestoneIcon className="h-4 w-4 flex-none text-tertiary" />
						{editingId === milestone.id ? (
							<Input
								name="milestone-name"
								autoFocus
								value={editName}
								maxLength={80}
								className="h-8 flex-1"
								onChange={(event) => setEditName(event.target.value)}
								onBlur={() => submitRename(milestone.id, milestone.name)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										submitRename(milestone.id, milestone.name)
									}
									if (event.key === "Escape") setEditingId(null)
								}}
							/>
						) : (
							<button
								type="button"
								disabled={isViewer}
								className="min-w-0 flex-1 truncate text-left text-sm text-primary"
								onClick={() => {
									setEditingId(milestone.id)
									setEditName(milestone.name)
								}}
							>
								{milestone.name}
							</button>
						)}
						<MilestoneDate
							value={
								milestone.targetDate
									? parseISO(milestone.targetDate)
									: undefined
							}
							disabled={isViewer}
							onChange={(targetDate) =>
								void write(() =>
									update.mutateAsync({
										projectId: project.id,
										milestoneId: milestone.id,
										targetDate,
									}),
								)
							}
						/>
						{!isViewer && (
							<IconButton
								variant="ghost"
								size="sm"
								Icon={TrashIcon}
								aria-label="Remove milestone"
								className="opacity-0 focus-visible:opacity-100 group-hover/milestone:opacity-100"
								onClick={() =>
									void write(() =>
										remove.mutateAsync({
											projectId: project.id,
											milestoneId: milestone.id,
										}),
									)
								}
							/>
						)}
					</div>
				))}
				{pending && (
					<div className="flex h-10 items-center gap-2.5 opacity-60">
						<MilestoneIcon className="h-4 w-4 flex-none text-tertiary" />
						<span className="min-w-0 flex-1 truncate text-sm text-primary">
							{pending.name}
						</span>
						<span className="flex h-7 w-28 flex-none items-center gap-1.5 px-2 text-sm tabular-nums text-secondary">
							<TargetIcon className="h-3.5 w-3.5 flex-none" />
							{pending.targetDate
								? format(parseISO(pending.targetDate), "MMM d")
								: "Target date"}
						</span>
						<span className="h-7 w-7 flex-none" />
					</div>
				)}
				{draft && (
					<div className="flex h-12 items-center gap-2">
						<Input
							name="new-milestone-name"
							autoFocus
							value={draft.name}
							maxLength={80}
							placeholder="Milestone name"
							className="flex-1"
							onChange={(event) =>
								setDraft({ ...draft, name: event.target.value })
							}
							onKeyDown={(event) => {
								if (event.key === "Enter") submitDraft()
								if (event.key === "Escape") setDraft(null)
							}}
						/>
						<MilestoneDate
							value={draft.targetDate ? parseISO(draft.targetDate) : undefined}
							onChange={(targetDate) =>
								setDraft({ ...draft, targetDate: targetDate ?? undefined })
							}
						/>
						<Button
							variant="default"
							disabled={!draft.name.trim()}
							onClick={submitDraft}
						>
							Add
						</Button>
						<IconButton
							variant="ghost"
							Icon={RemoveIcon}
							aria-label="Cancel"
							onClick={() => setDraft(null)}
						/>
					</div>
				)}
				{milestones.length === 0 && !draft && !pending && (
					<button
						type="button"
						className="flex h-10 items-center gap-2.5 text-left text-sm text-tertiary hover:text-primary"
						onClick={() => setDraft({ name: "" })}
					>
						<MilestoneIcon className="h-4 w-4 flex-none" />
						Add a milestone
					</button>
				)}
			</div>
		</div>
	)
}
